import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MatchWave } from '@anon-chat/shared';
import { AdSlot } from '@/components/ad-slot';
import { Button, Screen } from '@/components/ui';
import { useTranslations, t, duration } from '@/shared/i18n';
import { useChatStore } from '@/store/chat.store';

/** Экран ожидания: пульсирующие круги и живой счётчик времени поиска. */
export function SearchScreen(): React.ReactNode {
  const dictionary = useTranslations();
  const search = useChatStore((state) => state.search);
  const cancelSearch = useChatStore((state) => state.cancelSearch);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const startedAt = search.startedAt ?? Date.now();
    const timer = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [search.startedAt]);

  const subtitle =
    search.wave > MatchWave.STRICT
      ? dictionary.search.subtitleExpanding
      : dictionary.search.subtitleSearching;

  return (
    <Screen className="items-center justify-center px-6">
      <div className="relative flex size-48 items-center justify-center">
        {[0, 0.8, 1.6].map((delay) => (
          <span
            key={delay}
            className="pulse-ring absolute size-24 rounded-full border border-accent/60"
            style={{ animationDelay: `${delay}s` }}
          />
        ))}
        <motion.div
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          className="relative flex size-24 items-center justify-center rounded-full bg-accent/15"
        >
          <span className="text-4xl">💬</span>
        </motion.div>
      </div>

      <h2 className="mt-8 text-center text-[20px] font-semibold text-text">
        {dictionary.search.title}
      </h2>
      <p className="mt-2 text-center text-[14px] text-text-muted">{subtitle}</p>

      <div className="mt-6 flex flex-col items-center gap-1 text-[13px] text-text-muted">
        {search.position > 0 ? (
          <span>{t(dictionary.search.position, { position: search.position })}</span>
        ) : null}
        <span>{t(dictionary.search.waiting, { duration: duration(seconds) })}</span>
      </div>

      {/*
        Рекламное место ожидания. Человек здесь ждёт и ничего не делает —
        объявление никому не мешает и видно дольше всего.
      */}
      <AdSlot name="search" className="mt-8 w-full" />

      <div className="mt-auto w-full pb-4 pt-10">
        <Button variant="secondary" onClick={() => void cancelSearch()}>
          {dictionary.search.cancel}
        </Button>
      </div>
    </Screen>
  );
}
